<template>
  <v-container fluid class="background-container">
    <!-- Main content row: Left column (form controls) + Right column (table) -->
    <v-row class="mt-0 flex-nowrap">
      <!-- Left column: Form controls (3 cols) -->
      <v-col cols="auto" style="width: 340px; padding-left: 30px">
        <!-- Search criteria card -->
        <v-card outlined class="subtle-white">
          <v-card-title class="text-h6 py-3 justify-center">
            Επιλογή Ιδρύματος
			  <v-btn
				  icon
		          small
				  color="primary"
		          :loading="exportLoading"
		          :disabled="!showResultsCard"
		          @click="exportToCSV"
		          title="Εξαγωγή σε CSV"
			  >
				  <v-icon>mdi-download</v-icon>
			  </v-btn>
		  </v-card-title>
          <v-divider />
          <v-card-text>
            <!-- Institution autocomplete -->
            <v-autocomplete
              :items="$store.getters.institutions"
              v-model="institutionId"
              item-text="name"
              item-value="id"
              label="Ίδρυμα"
              @change="academicId = ''"
              outlined
              dense
              class="mb-0"
            />

            <!-- Department autocomplete -->
            <v-autocomplete
              :items="departmentItems"
              v-model="academicId"
              item-text="department"
              item-value="id"
              label="Τμήμα"
              outlined
              dense
              class="mb-0"
            />

            <!-- Academic Year select -->
            <v-select
              :items="$store.getters.yearsInfo.yearsList"
              v-model="year"
              item-value="year"
              item-text="yearLabel"
              label="Ακαδημαϊκό Έτος"
              outlined
              dense
              class="mb-0"
            />

            <!-- Search button -->
            <v-btn
              @click="setSelectedElementsAndSearch"
              color="primary"
              block
              class="mt-3"
            >
              Αναζήτηση
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        class="flex-grow-1"
        style="padding-right: 30px; padding-left: 30px"
      >
        <v-card outlined class="subtle-white">
          <v-card-title
            v-if="showResultsCard"
            class="text-h6 py-3 d-flex justify-space-between align-center"
          >
            <v-spacer></v-spacer>
            <span
              >Πληροφορίες Μαθημάτων/Συγγραμμάτων για το ακαδημαϊκό έτος
              {{ academicPeriod }}</span
            >
            <v-spacer></v-spacer>
            <v-btn
              icon
              small
              @click="toggleSemestersExpanseCollapse"
              :title="toggle_flag ? 'Ανάπτυξη όλων' : 'Σύμπτυξη όλων'"
            >
              <v-icon>{{
                toggle_flag ? "mdi-arrow-expand" : "mdi-arrow-collapse"
              }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-title
            v-else-if="!showResultsCard"
            class="text-h6 py-3 justify-center"
          >
            Πληροφορίες Μαθημάτων/Συγγραμμάτων
          </v-card-title>
          <v-divider />
          <v-card-text v-if="showResultsCard" class="pa-0">
            <div v-if="semesterPanels.length === 0" class="text-center pa-6">
              <span class="text-subtitle-1">Δεν βρέθηκαν αποτελέσματα</span>
            </div>
            <v-expansion-panels v-else v-model="openSemesterPanels" multiple>
              <v-expansion-panel
                v-for="(panel, index) in semesterPanels"
                :key="index"
              >
                <v-expansion-panel-header class="subtle-white">
                  <span class="text-subtitle-2">
                    Εξάμηνο {{ panel.semester }}</span
                  >
                </v-expansion-panel-header>
                <v-expansion-panel-content class="subtle-white">
                  <div v-if="panel.items.length === 0" class="text-center pa-4">
                    <span class="text-body-2">Δεν βρέθηκαν μαθήματα</span>
                  </div>
                  <v-expansion-panels
                    v-else
                    class="ml-4"
                    multiple
                    v-model="openCoursePanels[index]"
                  >
                    <v-expansion-panel
                      v-for="(course, courseIndex) in panel.items"
                      :key="courseIndex"
                    >
                      <v-expansion-panel-header
                        class="subtle-white-2"
                        @click="fetchCourseDetails(course, index, courseIndex)"
                      >
                        <span class="text-subtitle-2">
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                                class="period-indicator"
                                :style="{
                                  backgroundColor: getPeriodColor(
                                    course.period
                                  ),
                                }"
                              ></span>
                            </template>
                            <span>{{ getPeriodLabel(course.period) }}</span>
                          </v-tooltip>
                          Μάθημα [{{ course.code }}]: {{ course.title }}
                          <v-tooltip
                            v-if="isCourseExpanded(index, courseIndex)"
                            top
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                                class="professor-info"
                              >
                                Διδάσκοντες
                              </span>
                            </template>
                            <span>{{
                              course.professor ||
                              "Δεν υπάρχουν διαθέσιμοι διδάσκοντες"
                            }}</span>
                          </v-tooltip>
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="subtle-white-2">
                        <div
                          v-if="
                            course.details &&
                            (course.details.books.length > 0 ||
                              course.details.freeBooks.length > 0)
                          "
                          class="subtle-white-1"
                        >
                          <!-- Regular Books List -->
                          <v-list
                            v-if="course.details.books.length > 0"
                            dense
                            style="padding: 0"
                          >
                            <v-list-item
                              v-for="book in course.details.books"
                              :key="book.book_seq"
                              class="text-left subtle-white"
                            >
                              <v-list-item-content class="align-start">
                                <v-list-item-title class="text-left">
                                  <span
                                    class="book-link"
                                    @click="openBookDialog(book.bookObject)"
                                  >
                                    {{ book.book_seq + 1 }}. Βιβλίο [{{
                                      book.id
                                    }}] {{ book.title }}
                                  </span>
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>

                          <!-- Free Books Section -->
                          <div
                            v-if="
                              course.details.freeBooks &&
                              course.details.freeBooks.length > 0
                            "
                            class="mt-3"
                          >
                            <v-divider></v-divider>
                            <div
                              class="text-subtitle-2 text-left pl-8 pt-2 pb-2 font-weight-medium"
                            >
                              {{
                                course.details.books.length === 0
                                  ? "Για το μάθημα προσφέρεται μονάχα δωρεάν διδακτικό υλικό"
                                  : "Πρόσθετο Διδακτικό Υλικό"
                              }}
                            </div>
                            <v-list dense style="padding: 0">
                              <v-list-item
                                v-for="freeBook in course.details.freeBooks"
                                :key="freeBook.book_seq"
                                class="text-left subtle-white"
                              >
                                <v-list-item-content class="align-start">
                                  <v-list-item-title class="text-left">
                                    <span
                                      class="book-link"
                                      @click="openBookDialog(freeBook)"
                                    >
                                      [{{ freeBook.id }}] {{ freeBook.title }}
                                    </span>
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </div>
                        </div>
                        <div
                          v-else-if="course.loading"
                          class="text-center pa-4"
                        >
                          <v-progress-circular
                            indeterminate
                            color="primary"
                          ></v-progress-circular>
                        </div>
                        <div
                          v-else-if="
                            course.details &&
                            course.details.books.length === 0 &&
                            course.details.freeBooks.length === 0
                          "
                          class="text-center pa-4"
                        >
                          <span class="text-body-2"
                            >Δεν προσφέρονται συγγράμματα</span
                          >
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import requests, {BASE_URL} from "@/requests";
import utilities from "@/utilities";

export default {
  name: "CoursesAndBookgroups",

  data: () => ({
    semesterPanels: [],
    openSemesterPanels: [], // Tracks which semester panels are expanded
    openCoursePanels: {}, // Tracks which course panels are expanded per semester

    institutionId: "",
    academicId: "",
    year: "",

    toggle_flag: true,

    selectedYear: "",
    selectedInstitutionId: "",
    selectedAcademicId: "",

    showResultsCard: false,

	  exportLoading: false,


	  // ✅ Cache για τα course details
	  courseDetailsCache: {},
  }),

  methods: {
    openBookDialog(book) {
      this.$store.commit("setSelectedBook", book);
      this.$store.commit("setBookInfoDialog", true);
    },

    yearsCallBack(response) {
      this.$store.commit("setYearsInfo", response);
    },

    secretariatAcademicsCallBack(resp) {
      this.$store.commit("setLoadingModal", false);
      this.$store.commit(
        "setInstitutions",
        JSON.parse(JSON.stringify(resp.institutions))
      );
      this.$store.commit(
        "setAcademicSecretariats",
        JSON.parse(JSON.stringify(resp.academicSecretariats))
      );
      this.$store.commit(
        "setInstitutionAcademics",
        JSON.parse(JSON.stringify(resp.institutionAcademics))
      );
    },

    setSelectedElementsAndSearch() {
      if (this.year && this.institutionId && this.academicId) {
        ``;
        this.selectedYear = this.year;
        this.selectedInstitutionId = this.institutionId;
        this.selectedAcademicId = this.academicId;
      } else {
        utilities.setErrorPopup(
          "Σφάλμα",
          "Παρακαλώ συμπληρώστε όλα τα στοιχεία"
        );
        return; // return early in case of validation error
      }
      // Hide card before new search
      this.showResultsCard = false;

      this.getCoursesOfSemesters();
    },

    getCoursesOfSemesters() {
      document.activeElement.blur();
      this.$store.commit("setLoadingModal", true);

      let secretariatId =
        this.$store.getters.academicSecretariats[this.selectedAcademicId].id; // is a map with key->academicSecretariats and value -> secretariat Obj
      let year = this.selectedYear;

      requests.get(
        `rest/courses-books/get-semesters-courses?secretariatId=${secretariatId}&year=${year}`,
        this.getCoursesOfSemestersCallback
      );
    },

    getCoursesOfSemestersCallback(response) {
      this.semesterPanels = [];
      this.openSemesterPanels = []; // Reset expanded panels on new search
      this.openCoursePanels = {}; // Reset course panels tracking

      Object.keys(response).forEach((semesterKey, index) => {
        this.semesterPanels.push({
          semester: parseInt(semesterKey),
          items: response[semesterKey],
        });
        // Initialize empty array for each semester's course panels
        this.$set(this.openCoursePanels, index, []);
      });

      this.$store.commit("setLoadingModal", false);

      // Show card after response received
      this.showResultsCard = true;
    },

	  fetchCourseDetails(course, semesterIndex, courseIndex) {
		  // Skip if already loaded or currently loading
		  if (course.details || course.loading) {
			  return;
		  }

		  // ✅ Check cache first
		  const cacheKey = `${this.selectedAcademicId}_${this.selectedYear}_${course.id}`;

		  if (this.courseDetailsCache[cacheKey]) {
			  // Use cached data - no request needed!
			  this.$set(course, "details", this.courseDetailsCache[cacheKey]);
			  return;
		  }

		  // Set loading flag using Vue.set for reactivity
		  this.$set(course, "loading", true);

		  requests.get(
			  `rest/courses-books/course/${course.id}/books`,
			  (response) => {
				  const details = this.structureResponse(response);

				  // ✅ Store in cache
				  this.courseDetailsCache[cacheKey] = details;

				  // Update course
				  this.$set(course, "details", details);
				  this.$set(course, "loading", false);
			  }
		  );
	  },

    structureResponse(response) {
      this.books = [];
      this.freeBooks = [];
      let seq = 0;
      let seq1 = 0;
      Object.values(response.bookgroups).forEach((bookgroup) => {
        bookgroup.books.forEach((bookItem) => {
          this.books.push(this.constructBookFromResponse(seq++, bookItem.book));
        });
      });
      Object.values(response.freeBooks).forEach((freeBook) => {
        this.freeBooks.push(this.constructBookFromResponse(seq1++, freeBook));
      });

      return { books: this.books, freeBooks: this.freeBooks };
    },

    constructBookFromResponse(seq, book) {
      return {
        book_seq: seq,
        bookObject: book,
        id: book.id,
        title: book.title,
      };
    },

    requiredRule(v) {
      if (v && Object.keys(v).length > 0) return true;
      return "Το πεδίο είναι υποχρεωτικό";
    },

	  async toggleSemestersExpanseCollapse() {
		  if (this.toggle_flag) {
			  // Expand all semester panels
			  this.openSemesterPanels = this.semesterPanels.map((_, index) => index);

			  // Collect all courses that don't have details yet
			  const coursesToFetch = [];

			  this.semesterPanels.forEach((panel, semesterIndex) => {
				  panel.items.forEach((course, courseIndex) => {
					  if (!course.details && !course.loading) {
						  // ✅ Check cache first
						  const cacheKey = `${this.selectedAcademicId}_${this.selectedYear}_${course.id}`;

						  if (this.courseDetailsCache[cacheKey]) {
							  // Use cached data immediately - no request needed!
							  this.$set(course, 'details', this.courseDetailsCache[cacheKey]);
						  } else {
							  // Need to fetch from server
							  coursesToFetch.push({ course, semesterIndex, courseIndex });
						  }
					  }
				  });
			  });

			  // If there are courses to fetch, use Promise.all
			  if (coursesToFetch.length > 0) {
				  this.$store.commit("setLoadingModal", true);

				  const promises = coursesToFetch.map(({ course }) =>
					  this.axios.get(`${BASE_URL}rest/courses-books/course/${course.id}/books`)
				  );

				  try {
					  const results = await Promise.all(promises);

					  results.forEach((response, i) => {
						  const { course } = coursesToFetch[i];
						  const details = this.structureResponse(response.data);

						  // ✅ Store in cache
						  const cacheKey = `${this.selectedAcademicId}_${this.selectedYear}_${course.id}`;
						  this.courseDetailsCache[cacheKey] = details;

						  this.$set(course, 'details', details);
					  });
				  } catch (error) {
					  console.error('Error fetching course details:', error);
				  } finally {
					  this.$store.commit("setLoadingModal", false);
				  }
			  }

			  // Expand all course panels
			  this.semesterPanels.forEach((panel, index) => {
				  this.$set(this.openCoursePanels, index, panel.items.map((_, i) => i));
			  });

		  } else {
			  this.openSemesterPanels = [];
			  this.semesterPanels.forEach((_, index) => {
				  this.$set(this.openCoursePanels, index, []);
			  });
		  }

		  this.toggle_flag = !this.toggle_flag;
	  },

	  isCourseExpanded(semesterIndex, courseIndex) {
      // Check if the course panel at the given indices is expanded
      return (
        this.openCoursePanels[semesterIndex] &&
        this.openCoursePanels[semesterIndex].includes(courseIndex)
      );
    },

    getPeriodColor(period) {
      // Return color based on period value
      if (!period) return "#9E9E9E"; // Grey for undefined/null

      const periodLower = period.toLowerCase();

      if (
        periodLower.includes("χειμερινό") ||
        periodLower.includes("ximerino")
      ) {
        return "#2196F3"; // Blue
      } else if (
        periodLower.includes("εαρινό") ||
        periodLower.includes("earino")
      ) {
        return "#4CAF50"; // Green
      } else if (
        periodLower.includes("ετήσιο") ||
        periodLower.includes("etisio")
      ) {
        return "#FF9800"; // Orange
      }

      return "#9E9E9E"; // Default grey for unknown periods
    },

    getPeriodLabel(period) {
      return utilities.getPeriodLabel(period);
    },

	  exportToCSV() {
		  // Validation
		  if (!this.selectedAcademicId || !this.selectedYear) {
			  utilities.setErrorPopup("Σφάλμα", "Παρακαλώ κάντε πρώτα αναζήτηση");
			  return;
		  }

		  const secretariatId = this.$store.getters.academicSecretariats[this.selectedAcademicId].id;
		  const year = this.selectedYear;

		  if (!secretariatId || !year) {
			  return;
		  }

		  // Start loading
		  this.exportLoading = true;

		  const path = `rest/courses-books/book/eudoxus/extract-csv?secretariatId=${secretariatId}&year=${year}`;

		  // Use axios to fetch the file as blob
		  this.axios
			  .get(BASE_URL + path, {
				  responseType: "blob",
			  })
			  .then((response) => {
				  // Get filename from headers or use default
				  const contentDisposition = response.headers["content-disposition"];
				  let filename = `courses_export_${year}.csv`;

				  if (contentDisposition) {
					  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
					  if (filenameMatch && filenameMatch[1]) {
						  filename = filenameMatch[1].replace(/['"]/g, "");
					  }
				  }

				  // Create blob and trigger download
				  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
				  const url = window.URL.createObjectURL(blob);
				  const link = document.createElement("a");
				  link.href = url;
				  link.download = filename;
				  document.body.appendChild(link);
				  link.click();
				  document.body.removeChild(link);
				  window.URL.revokeObjectURL(url);

				  // Stop loading AFTER download triggered
				  this.exportLoading = false;
			  })
			  .catch((error) => {
				  console.error("CSV export error:", error);
				  utilities.setErrorPopup("Σφάλμα", "Αποτυχία λήψης αρχείου CSV");
				  this.exportLoading = false;
			  });
	  },

  },

  computed: {
    departmentItems() {
      let institutionAcademics = this.$store.getters.institutionAcademics;
      return institutionAcademics[this.institutionId]; // key->institutionId, value: List of Academic objects
    },

    academicPeriod() {
      if (!this.selectedYear) return "";
      return `${this.selectedYear}-${this.selectedYear + 1}`;
    },
  },

  created() {
    this.$store.commit("setLoadingModal", true);

    requests.getYears(this.yearsCallBack);
    requests.get(
      "rest/courses-books/secretariat-academics",
      this.secretariatAcademicsCallBack
    );
  },
  beforeDestroy() {},
};
</script>

<style scoped>
/* Component-specific styles */
.book-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
}

.book-link:hover {
  text-decoration: underline;
  color: #1565c0;
}

.period-indicator {
  display: inline-block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

.professor-info {
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  cursor: help;
  margin-left: 8px;
}
</style>
